import {
	isIsoDate,
	formatDate,
	formatDateRange,
	type IsoDate,
} from '@/lib/dates';
import type { IDirectDiscount } from '@/types/content';
import type { ILineItem } from '@/types/pricing';

/**
 * Everything the booking widgets know about the stay a guest is putting
 * together: dates, party size, and the price we have worked out for it.
 * Held in page state (`useState`) and passed down, never global.
 */
export interface IStay {
	currency?: string;
	startDate?: IsoDate;
	endDate?: IsoDate;
	adults: number;
	children: number;
	infants: number;
	/** Only where the property charges a pet fee. */
	pets?: number;
	lineItems?: ILineItem[];
	baseStayPrice?: number;
	totalBeforeDiscount?: number;
	totalToPayToday?: number;
	isDateSelected?: boolean;
	hasAvailability?: boolean;
	minStay?: number;
	appliedDiscount?: IDirectDiscount | null;
	error?: string;
}

/** True once the guest has picked dates we can price. */
export const hasSelectedStay = (stay: IStay) =>
	Boolean(stay.isDateSelected && stay.hasAvailability);

export const totalGuests = (stay: Pick<IStay, 'adults' | 'children'>) =>
	stay.adults + stay.children;

export const validateStayDates = (checkIn: unknown, checkOut: unknown) =>
	isIsoDate(checkIn) && isIsoDate(checkOut);

export const formatStayDates = (
	checkIn?: string,
	checkOut?: string,
	locale = 'en',
	fallback = 'No dates selected',
) =>
	isIsoDate(checkIn) && isIsoDate(checkOut)
		? formatDateRange(checkIn, checkOut, locale)
		: fallback;

export const formatSingleStayDate = (
	date?: string,
	locale = 'en',
	fallback = 'No dates selected',
) => (isIsoDate(date) ? formatDate(date, locale) : fallback);

/** True when the party fits the property. */
export const totalGuestsLessThanCapacity = (
	capacity: number,
	adults: number,
	children: number,
) => adults + children <= capacity;

/** True when check-out is after check-in. */
export const datesSequential = (first: string, second: string): boolean =>
	validateStayDates(first, second) && first < second;
